import { Flex, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPaperclip } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FilesModal from "src/components/shared/_Modals/FilesModal/FilesModal";
import { useFilesModal } from "src/components/shared/_Modals/FilesModal/FilesModal.hooks";
import { Button } from "src/components/shared/Button/Button";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { useCheckPerm, useGetApi, usePostApi } from "src/hooks";
import { type IFile } from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { StyledTd, StyledTr } from "src/styled";

import { ReviewText } from "../ReviewsTable/ReviewsTable.styled";
import { type IRevAud } from "./RevAud.types";
import { getReviewType, renderRevAudStatus } from "./RevAud.utils";

interface Props {
  row: IRevAud;
  refetch: () => Promise<void>;
  reviewerCell?: { label: string };
}

const filesMapper = (data: any): IFile[] => {
  if (!data.success) return [];
  return data.list as IFile[];
};

const RevAudItemRow = ({ row, refetch, reviewerCell }: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAuditsPage = useMemo(() => pathname.includes("audits"), [pathname]);
  const [filesOpened, { open: openFiles, close: closeFiles }] =
    useDisclosure(false);
  const checkPerm = useCheckPerm();
  const yachtuser = useSelector(state => state.permsSlice.yachtuser);

  const { data: files, refetch: fetchFiles } = useGetApi({
    request: "get-audit-files",
    body: { params: { aid: row.id } },
    fetchOnInit: false,
    mapper: filesMapper,
  });

  const { isUploading, showRemoved, toggleRemoved, _files, removedNumber } =
    useFilesModal(files ?? []);

  const { post, isLoading } = usePostApi();

  const handleOpenFiles = useCallback(async () => {
    await fetchFiles();
    openFiles();
  }, [fetchFiles, openFiles]);

  const handleRevoke = useCallback(
    (id: string) => {
      return async () => {
        await post({ request: "revoke-review", body: { params: { id } } });
        await refetch();
      };
    },
    [post, refetch],
  );

  return (
    <>
      {checkPerm("viewfiles") && (
        <FilesModal
          opened={filesOpened}
          close={closeFiles}
          isUploading={isUploading}
          showRemoved={showRemoved}
          toggleRemoved={toggleRemoved}
          removedNumber={removedNumber}
          _files={_files}
          readonly
        />
      )}
      <StyledTr key={row.id} hover background="transparent">
        <StyledTd>
          <ReviewText blue>{getReviewType(row.type)}</ReviewText>
          {!yachtuser ? (
            <Text ml={15} mb={5}>
              {row.yachtname}
            </Text>
          ) : row.full_name && row.email ? (
            <Text ml={15} mb={5}>{`${row.full_name} / ${row.email}`}</Text>
          ) : null}
          {row.logbooks?.map(lbname => (
            <Text key={lbname} ml={15}>
              {lbname}
            </Text>
          ))}
        </StyledTd>
        <StyledTd center>
          <ReviewText>{dayjs(row.start).format("D MMM YYYY")}</ReviewText>
        </StyledTd>
        <StyledTd center>
          <ReviewText>{dayjs(row.end).format("D MMM YYYY")}</ReviewText>
        </StyledTd>
        <StyledTd center>
          <ReviewText>{dayjs(row.revstart).format("D MMM YYYY")}</ReviewText>
        </StyledTd>
        <StyledTd center>
          <ReviewText>{dayjs(row.revend).format("D MMM YYYY")}</ReviewText>
        </StyledTd>
        <StyledTd center>
          <ReviewText>
            {row.completeddate
              ? dayjs(row.completeddate).format("D MMM YYYY")
              : "--------------"}
          </ReviewText>
        </StyledTd>
        <StyledTd center>{renderRevAudStatus(row)}</StyledTd>
        {reviewerCell && <StyledTd center></StyledTd>}
        <StyledTd style={{ borderLeft: "none" }}>
          <Flex gap="sm" direction="column">
            {yachtuser && row.status === 0 && !row.revoked && !row.ended && (
              <ConfirmationButton
                modalText="Are you sure you want to revoke this review?"
                buttonProps={{
                  w: 130,
                  mt: "auto",
                  color: "red.9",
                  loading: isLoading,
                }}
                onConfirm={handleRevoke(row.id)}>
                Revoke
              </ConfirmationButton>
            )}
            <Button
              onClick={() =>
                navigate(
                  `/summary/${
                    isAuditsPage ? "audits/audit" : "reviews/review"
                  }/${row.id}/full-report/${row.yachtid}`,
                )
              }>
              Report
            </Button>
            <Button
              onClick={() =>
                navigate(
                  `/summary/${
                    isAuditsPage ? "audits/audit" : "reviews/review"
                  }/${row.id}`,
                )
              }>
              View
            </Button>
            {checkPerm("viewfiles") && row.attachments.length > 0 && (
              <Button onClick={handleOpenFiles}>
                <Flex gap={6}>
                  <IconPaperclip size={16} />
                  Attachments
                </Flex>
              </Button>
            )}
          </Flex>
        </StyledTd>
      </StyledTr>
    </>
  );
};

export default RevAudItemRow;
